import { Search } from '@mui/icons-material';
import { Box, Container, Grid, Skeleton, Stack, styled, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { every, get, isString } from 'lodash';
import Lottie from 'react-lottie';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';

import { StyledWhiteBox } from 'src/features/TariffsAndRules/config';
import ImportDuty from 'src/features/TariffsAndRules/ImportDuty';
import RegulatoryRequirement from 'src/features/TariffsAndRules/RegulatoryRequirements';
import TradeRemedies from 'src/features/TariffsAndRules/TradeRemedies';

// Utilities
import { useEffect, useMemo } from 'react';
import SearchIcon from 'src/asset/lottie/searches.json';
import ImageComponent from 'src/components/common/Image';
import PublicPageContainer from 'src/features/Public/PublicPageContainer';
import CustomsTariff from 'src/features/Public/TariffsAndRules/CustomsTariff';
import FormItemCountryTariffs from 'src/features/Public/TariffsAndRules/FormItemCountryTariffs';
import FormItemHsCode from 'src/features/Public/TariffsAndRules/FormItemHsCode';
import FormItemNationalTariffCode from 'src/features/Public/TariffsAndRules/FormItemNationalTariffCode';
import Overview from 'src/features/Public/TariffsAndRules/Overview';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryPublicGetCustomDuties,
  useQueryPublicGetNtmMeasures,
  useQueryPublicGetTradeRemedy,
} from 'src/services/queries/useQueryPublic';
import { EmptyText } from 'src/utils/constants/config';
import { TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES } from 'src/utils/constants/imagesUrl';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { HttpStatusCode } from 'axios';
import { IFrameType, usePopupRedirectStore } from 'src/zustand/usePopupRedirectStore';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const TariffsAndRulesPublic = () => {
  const [, setSearchParam] = useSearchParams();
  const { partner, reporter, hs_code, product, partner_country_code } = useQueryParams();
  const { dispatch: dispatchPopup } = usePopupRedirectStore();
  // const [visibleBanner, setVisibleBanner] = useState(true);
  // const { data, isFetching } = useQueryRuntimeConfig();
  const GetNtmMeasuresQuery = useQueryPublicGetNtmMeasures({
    reporter: reporter && isString(reporter) ? reporter : '',
    partner: partner && isString(partner) ? partner : '',
    product: product && isString(product) ? product : '',
  });
  const GetCustomDutiesQuery = useQueryPublicGetCustomDuties({
    reporter: reporter && isString(reporter) ? reporter : '',
    partner: partner && isString(partner) ? partner : '',
    product: product && isString(product) ? product : '',
  });
  const GetTradeRemedyQuery = useQueryPublicGetTradeRemedy({
    reporter: reporter && isString(reporter) ? reporter : '',
    partner: partner && isString(partner) ? partner : '',
    product: product && isString(product) ? product : '',
  });
  const dataNtmMeasures = GetNtmMeasuresQuery.data?.data;
  const dataCustomDuties = GetCustomDutiesQuery.data?.data;
  const dataTradeRemedy = GetTradeRemedyQuery.data?.data;

  useEffect(() => {
    if (
      [
        GetNtmMeasuresQuery?.data?.status_code,
        GetCustomDutiesQuery?.data?.status_code,
        GetTradeRemedyQuery?.data?.status_code,
      ].includes(HttpStatusCode.TooManyRequests)
    ) {
      dispatchPopup({ visible: true, iframe: IFrameType.TariffAndRules });
    }
  }, [GetNtmMeasuresQuery?.data, GetCustomDutiesQuery?.data, GetTradeRemedyQuery?.data]);

  const isEmptyData = useMemo(() => {
    const isDoneFetching =
      !GetNtmMeasuresQuery.isFetching && !GetCustomDutiesQuery.isFetching && !GetTradeRemedyQuery.isFetching;
    const isDataEmpty = !dataNtmMeasures && !dataCustomDuties && !dataTradeRemedy;
    return isDoneFetching && isDataEmpty;
  }, [GetNtmMeasuresQuery, GetCustomDutiesQuery, GetTradeRemedyQuery]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      country_partner: partner && isString(partner) ? partner : '',
      country_reporter: reporter && isString(reporter) ? reporter : '',
      hs_code: hs_code && isString(hs_code) ? hs_code : '',
      national_code: product && isString(product) ? product : '',
      partner_country_code: '',
    },
    validationSchema: Yup.object().shape({
      country_partner: Yup.string().required('This field is required').max(255),
      country_reporter: Yup.string().required('This field is required').max(255),
      hs_code: Yup.string().required('This field is required').max(255),
      national_code: Yup.string().required('This field is required').max(255),
      partner_country_code: Yup.string(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSearchParam({
        partner: values.country_partner,
        reporter: values.country_reporter,
        hs_code: values.hs_code,
        product: values.national_code,
        partner_country_code: values.partner_country_code,
      });
      setSubmitting(false);
    },
  });

  const renderContent = () => {
    if (!every([partner, reporter, hs_code, product], isString)) return null;

    if (isEmptyData) return <EmptyData />;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Stack spacing={3}>
            <Overview dataNtmMeasures={dataNtmMeasures} isFetching={GetNtmMeasuresQuery.isFetching} />
            <ImportDuty dataCustomDuties={dataCustomDuties} isFetching={GetCustomDutiesQuery.isFetching} />
            <StyledWhiteBox spacing={4} direction="row" alignItems="center">
              <Box display="flex" flex={1}>
                <ImageComponent imgSrc={TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES.saving_money_bank} width={150} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" color="text.secondary" align="center">
                  Trade Remedies
                </Typography>
                <Typography variant="h4" align="center">
                  {getArrayDefault(dataTradeRemedy?.trade_remedy_data).length}
                </Typography>
              </Box>
            </StyledWhiteBox>
            <StyledWhiteBox spacing={4} direction="row" alignItems="center">
              <Box display="flex" flex={1}>
                <ImageComponent imgSrc={TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES.supply_chain_distribution} width={150} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" color="text.secondary" align="center">
                  Requirements
                </Typography>
                <Typography variant="h4" align="center">
                  {get(dataNtmMeasures, '[0].measure_total_count', EmptyText)}
                </Typography>
              </Box>
            </StyledWhiteBox>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Stack spacing={3}>
            <CustomsTariff dataCustomDuties={dataCustomDuties} isFetching={GetCustomDutiesQuery.isFetching} />
            <TradeRemedies dataTradeRemedy={dataTradeRemedy} isFetching={GetCustomDutiesQuery.isFetching} />
            {GetNtmMeasuresQuery.isFetching ? (
              <Skeleton animation="wave" variant="rectangular" height={400} />
            ) : (
              getArrayDefault(dataNtmMeasures).map((measure, index) => {
                return <RegulatoryRequirement key={`${index}-${measure.measure_direction}`} dataNtmMeasure={measure} />;
              })
            )}
          </Stack>
        </Grid>
      </Grid>
    );
  };
  // const renderBannerTariffByUS = () => {
  //   const countryChargedByUS = find(data.US_TARIFFS, ({ country_code }) => country_code === partner_country_code);
  //   if (!countryChargedByUS || reporter !== '842' || !visibleBanner) return null;
  //   return (
  //     <StyledBoxBannerTariff px={4} py={1}>
  //       <ButtonWithLoading
  //         onClick={() => setVisibleBanner(false)}
  //         isRounded
  //         color="inherit"
  //         sx={{ position: 'absolute', top: 0, right: 0 }}
  //         variant="text"
  //       >
  //         <Close />
  //       </ButtonWithLoading>
  //       <Box width={120} component={'img'} src={TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES.us_tariff_banner} />
  //       <Box>
  //         <Typography mb={1} color={'secondary.main'} variant="h5">
  //           Check Latest Tariff Rates Before You Decide
  //         </Typography>
  //         <Typography variant="body1">
  //           New tariffs may apply based on recent trade policy changes. The current reciprocal tariff on{' '}
  //           {countryChargedByUS?.country_name} could be as high as {countryChargedByUS?.new_us_tariff}%. Stay informed
  //           and double-check tariff rates before make decision.
  //         </Typography>
  //       </Box>
  //     </StyledBoxBannerTariff>
  //   );
  // };

  return (
    <PublicPageContainer featureName={'Tariffs And Rules'}>
      <StyledContainer maxWidth={'xl'}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5">Tariffs and Rules Search</Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Identify import and export-related regulatory requirements applicable to a product by importer, exporter
              and year.
            </Typography>
          </Box>
          <FormikForm formik={formik}>
            <StyledWhiteBox>
              <Grid container rowSpacing={1.5} columnSpacing={2}>
                <Grid item xs={12} lg={6}>
                  <FormItemCountryTariffs
                    onChange={(value) => formik.setFieldValue('partner_country_code', value?.iso2)}
                    required={true}
                    id="country_partner"
                    label="Exporting Country"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormItemCountryTariffs required={true} id="country_reporter" label="Destination Country" />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormItemHsCode
                    countryCode={formik.values.country_reporter}
                    defaultKeyword={formik.values.hs_code}
                    required={true}
                    id="hs_code"
                    disabled={!formik.values.country_reporter}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormItemNationalTariffCode
                    countryCode={formik.values.country_reporter}
                    required={true}
                    hsCode={formik.values.hs_code}
                    id="national_code"
                    disabled={!formik.values.hs_code}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ButtonWithLoading
                    isRounded
                    fullWidth
                    size="large"
                    variant="contained"
                    startIcon={<Search />}
                    type="submit"
                    sx={{ paddingLeft: '22px', paddingRight: '22px' }}
                  >
                    Search
                  </ButtonWithLoading>
                </Grid>
              </Grid>
            </StyledWhiteBox>
          </FormikForm>
          {/* {renderBannerTariffByUS()} */}
        </Stack>
        <Box mt={3} />
        {renderContent()}
      </StyledContainer>
    </PublicPageContainer>
  );
};

export default TariffsAndRulesPublic;

const EmptyData = () => {
  return (
    <Stack>
      <Lottie
        options={{
          loop: true,
          autoplay: true,
          animationData: SearchIcon,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice',
          },
        }}
        height={600}
        width={540}
      />
      <Typography variant="h2" sx={{ color: '#3658C1' }} align="center">
        No Results Found
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center">
        Sorry, we did not find any results matching your search criteria. Please search again.
      </Typography>
    </Stack>
  );
};
